import axios from 'axios';
import { DataTypePlural, Filter, Data, MetadataResponse } from '@ilteducation/content-ref-data-types';
import { useConfigHelper } from '@ilteducation/config';

const { getApiUrl } = useConfigHelper();

export type Response<T> = {
  cursor: string | null;
  data: T[];
};

export const getRefDatas = <T>(
  type: DataTypePlural,
  cursor: string | null,
  filter?: Filter[],
  query?: string,
  /** The service returns 100 when this is left out. */
  limit?: number,
): Promise<Response<T>> => {
  const qsParts: string[] = [];
  if (limit) {
    qsParts.push(`limit=${limit}`);
  }
  // Both are free-form: the cursor is base64 (which can hold '+' and '='), and the query is
  // whatever the user typed. Unencoded, either can break the query string server-side.
  if (cursor) {
    qsParts.push(`cursor=${encodeURIComponent(cursor)}`);
  }
  if (query) {
    qsParts.push(`q=${encodeURIComponent(query)}`);
  }
  if (filter) {
    qsParts.push(...filter.flatMap((f) => f.value.map((val) => encodeURI(`filter=${f.key}:${val}`))));
  }
  const queryString = qsParts.length ? `?${qsParts.join('&')}` : '';
  const url = `${getApiUrl('content-ref-data', true)}/${type}/search${queryString}`;
  return axios.get(url).then((response) => response.data as Response<T>);
};

/**
 * A page of a type straight off its collection, for one content-ref-data has no
 * search configuration for.
 *
 * Contributors are that type: every other one is listed from `/{type}/search`,
 * and that path does not answer for this one at all. The plain collection does,
 * with the same cursor pagination — but no `q` and no `filter`, which is why
 * the contributor list has no search box.
 */
export const getRefDataPage = <T>(type: DataTypePlural, cursor: string | null, limit = 100): Promise<Response<T>> => {
  const qsParts = [`limit=${limit}`];
  // The cursor is base64, so it can hold '+' and '=' — unencoded, either ends
  // the query string early.
  if (cursor) qsParts.push(`cursor=${encodeURIComponent(cursor)}`);

  return axios
    .get(`${getApiUrl('content-ref-data', true)}/${type}?${qsParts.join('&')}`)
    .then((response) => response.data as Response<T>);
};

export const getRefData = <T>(type: DataTypePlural, contentId: string): Promise<T> =>
  axios.get(`${getApiUrl('content-ref-data', true)}/${type}/${contentId}`).then((response) => response.data as T);

function chunkArray<T>(array: T[], chunkSize: number) {
  const result = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    const chunk = array.slice(i, i + chunkSize);
    result.push(chunk);
  }
  return result;
}

export const getRefDataByIds = async (contentIds: string[]): Promise<Data[]> => {
  const results: Data[] = [];

  // content-ref-data/byIds can only handle 100 ids at a time
  const promises = chunkArray(contentIds, 100).map((chunk) =>
    axios
      .get(`${getApiUrl('content-ref-data', true)}/byIds?ids=${chunk.join(',')}`)
      .then((response) => results.push(...(response.data.data as Data[]))),
  );

  await Promise.all(promises);
  return results;
};

/* -------------------------------------------------------------------------
   Bibliographic metadata

   content-ref-data fronts Bokinfo, the Swedish book-trade register, and the
   ONIX documents publishers send. Neither of these reads or writes a
   reference-data record — they resolve an ISBN, or parse a file, into the
   shape a new book starts from — which is why they sit at the package root
   rather than behind `/admin`.
   ------------------------------------------------------------------------- */

const metadataUrl = () => `${getApiUrl('content-ref-data', true)}/metadata`;

/**
 * Everything Bokinfo knows about an ISBN, as a partial book.
 *
 * `Metadata` is `NewBookV1` without the four fields only this organisation can
 * decide — market, availability, products and properties — so the result is
 * merged into a draft rather than replacing it.
 *
 * Resolves to `null` when the register has no such ISBN, which is an ordinary
 * outcome for a title that has not been registered yet, not an error.
 */
export const getBookMetadata = (isbn: string): Promise<MetadataResponse | null> =>
  axios
    .get(`${metadataUrl()}/${encodeURIComponent(isbn)}`)
    .then((response) => (response.data as MetadataResponse) ?? null)
    .catch((error) => {
      if (axios.isAxiosError(error) && error.response?.status === 404) return null;
      throw error;
    });

/**
 * The same shape, from an ONIX document rather than from a lookup.
 *
 * The body is the XML itself, not JSON — the service parses what it is sent,
 * so the content type has to say so or it is rejected as a malformed request.
 */
export const parseBookMetadata = (document: string, format = 'onix'): Promise<MetadataResponse> =>
  axios
    .post(`${metadataUrl()}/parse`, document, {
      params: { format },
      headers: { 'Content-Type': 'text/xml' },
    })
    .then((response) => response.data as MetadataResponse);
